import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck,
  Plus,
  Trash2,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock,
  X,
  Search,
  Loader2,
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

// ---- Types ----------------------------------------------------------------

interface WhitelistEntry {
  id: number;
  ip: string;
  label: string;
  active: boolean;
  status: "active" | "pending";
  created_at: string | null;
}

interface ListResponse {
  total: number;
  active: number;
  pending: number;
  entries: WhitelistEntry[];
}

// ---- Helpers --------------------------------------------------------------

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const isValidIp = (s: string): boolean => {
  const v4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
  const v6 = /^[0-9a-fA-F:]+$/;
  const trimmed = (s || "").trim();
  if (!trimmed) return false;
  if (v4.test(trimmed)) return true;
  if (trimmed.length > 5 && trimmed.length <= 45 && v6.test(trimmed) && trimmed.includes(":")) return true;
  return false;
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ---- Component ------------------------------------------------------------

const IpWhitelist: React.FC = () => {
  const [entries, setEntries] = useState<WhitelistEntry[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  // Add form
  const [addOpen, setAddOpen] = useState(false);
  const [newIp, setNewIp] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<WhitelistEntry | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Toast
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const fetchEntries = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await axios.get<ListResponse>(`${API_URL}/developers/ip-whitelist`, {
        headers: authHeaders(),
      });
      setEntries(res.data.entries || []);
      setStats({ total: res.data.total, active: res.data.active, pending: res.data.pending });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load your IP whitelist");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchEntries(false); }, [fetchEntries]);

  const showToast = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const submitAdd = async () => {
    setFormError(null);
    const ip = newIp.trim();
    if (!ip) { setFormError("IP address is required"); return; }
    if (!isValidIp(ip)) { setFormError("Enter a valid IPv4 or IPv6 address"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/developers/ip-whitelist`, {
        ip, label: newLabel.trim() || null,
      }, { headers: authHeaders() });
      setAddOpen(false);
      setNewIp(""); setNewLabel("");
      showToast("ok", "Whitelist request submitted. A Moko admin will activate it shortly.");
      fetchEntries(true);
    } catch (e: any) {
      setFormError(e?.response?.data?.detail || e?.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/developers/ip-whitelist/${deleteTarget.id}`, {
        headers: authHeaders(),
      });
      setDeleteTarget(null);
      showToast("ok", "Pending request removed.");
      fetchEntries(true);
    } catch (e: any) {
      showToast("err", e?.response?.data?.detail || e?.message || "Failed to remove entry");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return entries;
    return entries.filter(e => e.ip.toLowerCase().includes(s) || (e.label || "").toLowerCase().includes(s));
  }, [entries, search]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            IP Whitelist
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            List of IPs allowed to call the Moko API on behalf of your merchant account.
            You can request additions from here — a Moko admin activates each new IP after review.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchEntries(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={() => { setAddOpen(true); setFormError(null); setNewIp(""); setNewLabel(""); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 shadow"
          >
            <Plus className="w-4 h-4" />
            Add IP
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Total</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</p>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="text-xs uppercase tracking-wider text-emerald-700 font-medium">Active</p>
          <p className="text-2xl font-bold text-emerald-800 mt-1">{stats.active}</p>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs uppercase tracking-wider text-amber-700 font-medium">Pending review</p>
          <p className="text-2xl font-bold text-amber-800 mt-1">{stats.pending}</p>
        </div>
      </div>

      {/* Search + table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search IP or label…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              {error}
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            {entries.length === 0
              ? "No whitelisted IPs yet. Click Add IP to submit your first."
              : "No entries match this search."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">IP</th>
                  <th className="px-4 py-3 text-left font-semibold">Label</th>
                  <th className="px-4 py-3 text-left font-semibold">Status</th>
                  <th className="px-4 py-3 text-left font-semibold">Requested at</th>
                  <th className="px-4 py-3 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3">
                      <span className="font-mono text-slate-800">{e.ip}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-700 max-w-[280px] truncate" title={e.label}>
                      {e.label || <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {e.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 border border-emerald-200">
                          <CheckCircle className="w-3 h-3" /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          <Clock className="w-3 h-3" /> Pending admin review
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      {!e.active ? (
                        <button
                          onClick={() => setDeleteTarget(e)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded border border-red-200 text-red-700 hover:bg-red-50 transition"
                        >
                          <Trash2 className="w-3 h-3" /> Cancel
                        </button>
                      ) : (
                        <span
                          className="text-xs text-slate-400"
                          title="Active entries can only be disabled by Moko admins"
                        >
                          Contact support to remove
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add modal */}
      {addOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-900">Add IP to whitelist</h2>
              <button
                onClick={() => setAddOpen(false)}
                className="p-1 rounded-md hover:bg-slate-100"
              >
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3">
                Your request will be sent to a Moko admin for review. Once approved,
                the IP will be able to reach the API on behalf of your merchant account.
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">IP address *</label>
                <input
                  type="text"
                  value={newIp}
                  onChange={(e) => setNewIp(e.target.value)}
                  placeholder="e.g. 41.243.11.42"
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono outline-none focus:ring-2 focus:ring-emerald-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Label (optional)</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Production checkout server"
                  maxLength={64}
                  className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <p className="text-[11px] text-slate-400 mt-1">Up to 64 characters. Helps admins identify the source.</p>
              </div>
              {formError && (
                <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-2.5 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {formError}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setAddOpen(false)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={submitAdd}
                disabled={submitting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Submit request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-4">
            <h2 className="text-base font-semibold text-slate-900">Cancel pending request</h2>
            <p className="text-sm text-slate-600">
              Do you want to cancel your pending whitelist request for{" "}
              <span className="font-mono text-slate-900">{deleteTarget.ip}</span>?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                Keep it
              </button>
              <button
                onClick={submitDelete}
                disabled={deleting}
                className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                Cancel request
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg shadow-lg text-sm z-50 max-w-md ${
          toast.kind === "ok"
            ? "bg-emerald-600 text-white"
            : "bg-red-600 text-white"
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};

export default IpWhitelist;
